import React from 'react';
import { ShadedBox } from '../../Common/ShadedBox';
import { PlanCard } from '~/types/models/plasmic';
import cx from 'classnames';
import { PricingPanel } from './PricingPanel';
import { InclusionsPanel } from './InclusionsPanel';
import { PricingCtaPanel, PricingCtaShowType } from './PricingCtaPanel';
import { BadgeBar } from './BadgeBar';
import { PricingFooter } from './PricingFooter';
import {
  AnimatedDiv,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import { getFormattedPrice } from '../../../utils';
import { BillingCycle } from '~/types/models';

export enum PricingPlanCardLayout {
  Portrait = 'portrait',
  Landscape = 'landscape',
}

export interface PricingPlanCardProps extends PlanCard {
  billingCycle?: BillingCycle;
  discounted?: boolean;
  showCurrentPlanTheme?: boolean;
  hideCta?: boolean;
  layout?: PricingPlanCardLayout;
  onCtaClick?: () => void;
  showImage?: boolean;
}

export function PricingPlanCard({
  layout = PricingPlanCardLayout.Portrait,
  service,
  discounted = false,
  ctaVariant,
  badge,
  title,
  details,
  image,
  inclusions,
  bottomBorderColor,
  billingCycle,
  showCurrentPlanTheme,
  hideCta = false,
  onCtaClick,
  showImage = true,
}: PricingPlanCardProps) {
  const isPortrait = layout === PricingPlanCardLayout.Portrait;
  const isLandscape = layout === PricingPlanCardLayout.Landscape;

  return (
    <AnimatedDiv show showAnimation={ShowAnimation.SlideInDown150}>
      <ShadedBox className='flex flex-col rounded-md !p-0'>
        {badge && <BadgeBar badge={badge} />}
        <div
          className={cx(
            ['flex flex-col items-center', 'justify-between relative'],
            {
              'w-[262px] h-[525px]': isPortrait,
              'v2-bg-blue-50': showCurrentPlanTheme && isPortrait,
              'w-[490px]': isLandscape,
              'v2-bg-magenta-50': showCurrentPlanTheme && isLandscape,
            }
          )}
        >
          <div
            className={cx('flex flex-col w-full z-10', {
              'p-4 gap-y-4': isPortrait,
              'p-6 gap-y-[18px]': isLandscape,
            })}
          >
            <div className='flex flex-row gap-x-4 w-full'>
              {isLandscape && (
                <img src={image?.url} className='h-[165px] w-[236px]' />
              )}
              <div
                className={cx('w-full flex flex-col', {
                  'items-center gap-y-2.5': isPortrait,
                  'items-start gap-y-3': isLandscape,
                })}
              >
                <span
                  className='font-bold text-[21px] leading-4'
                  style={{ color: title?.textColor }}
                >
                  {title.label}
                </span>
                <span
                  className={cx(
                    'text-base w-[223px] leading-[1.75] v2-text-neutral-900',
                    {
                      'text-center': isPortrait,
                      'text-left': isLandscape,
                    }
                  )}
                >
                  {details.description}
                </span>
                <PricingPanel
                  discounted={discounted}
                  service={service}
                  monthlyPrice={details.monthlyPrice}
                  annualPrice={details.annualPrice}
                  billingCycle={billingCycle}
                />
                {isLandscape && (
                  <span className='text-xs v2-text-neutral-500'>
                    {billingCycle === BillingCycle.Monthly
                      ? `Billed $${getFormattedPrice(
                          details.monthlyPrice
                        )} USD monthly`
                      : `Billed $${getFormattedPrice(
                          details.annualPrice
                        )} USD annually`}
                  </span>
                )}
              </div>
            </div>
            {!hideCta && (
              <PricingCtaPanel
                showType={
                  showCurrentPlanTheme
                    ? PricingCtaShowType.Placeholder
                    : PricingCtaShowType.Button
                }
                variant={ctaVariant}
                onCtaClick={onCtaClick}
                service={service}
              />
            )}
            {inclusions && (
              <InclusionsPanel
                inclusions={inclusions}
                service={service}
                className={cx({
                  '!gap-y-2.5': isLandscape,
                })}
              />
            )}
          </div>
          {showImage && (
            <PricingFooter
              image={image}
              bottomBorderColor={bottomBorderColor}
            />
          )}
        </div>
      </ShadedBox>
    </AnimatedDiv>
  );
}
